import { Alert } from '@mui/material';
import { useAssignTransactionMutation } from 'api/transactions/transactions';
import { useListStoresQuery } from 'api/client-domain/client-domain';
import AutocompleteField from 'components/forms/fields/autocomplete-field/autocomplete-field.component';
import IncTextField from 'components/forms/fields/inc-text-field/inc-text-field.component';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import FormDrawer from '../form-drawer/form-drawer.component';
import { useAttachTransactionForm } from './attach-transaction-drawer.hooks';

type AttachTransactionDrawerProps = {
  open: boolean;
  close: () => void;
  drawerData?: {
    customerId: string;
  };
};

const AttachTransactionDrawer = ({
  open,
  close,
  drawerData,
}: AttachTransactionDrawerProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const form = useAttachTransactionForm();

  const { data } = useListStoresQuery({ count: 1000, page: 0 });
  const locationOptions = useMemo(
    () =>
      data?.stores?.map(store => ({
        id: store.storeId,
        label: store.title,
      })),
    [data?.stores],
  );

  const [assignTransaction, { isLoading, isSuccess }] =
    useAssignTransactionMutation();

  const onClose = () => {
    form.reset();
    close();
  };

  const submit = async ({ checkId, locationId }) => {
    try {
      await assignTransaction({
        locationId,
        extTxnId: checkId,
        userId: drawerData?.customerId,
      }).unwrap();
      onClose();
    } catch (_e) {}
  };

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar(t('drawer.attachTransactionDrawer.toast.success'), {
        severity: 'success',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      cancel={onClose}
      onValid={submit}
      title={t('drawer.attachTransactionDrawer.title')}
      submitButtonLabel={t('drawer.attachTransactionDrawer.buttons.submit')}
      formMethods={form}
      isLoading={isLoading}
      data-testid="AttachTransactionDrawer.Drawer"
    >
      <Alert severity="info" data-testid="AttachTransactionDrawer.Alert">
        {t('drawer.attachTransactionDrawer.infoText')}
      </Alert>
      <IncTextField
        name="checkId"
        label={t('drawer.attachTransactionDrawer.fields.checkId.label')}
        helperText={t(
          'drawer.attachTransactionDrawer.fields.checkId.helperText',
        )}
        data-testid="AttachTransactionDrawer.IncTextField.CheckId"
      />
      <AutocompleteField
        name="locationId"
        label={t('drawer.attachTransactionDrawer.fields.location.label')}
        options={locationOptions}
        TextFieldProps={{
          placeholder: t(
            'drawer.attachTransactionDrawer.fields.location.placeholder',
          ),
        }}
        data-testid="AttachTransactionDrawer.AutocompleteField.Location"
      />
    </FormDrawer>
  );
};

export default AttachTransactionDrawer;
